import React, {useState, useEffect, useCallback, useRef} from 'react';
import _ from 'lodash';
import classnames from 'classnames/bind';
import {
  IMake,
  IModel,
  IVehicleCategory,
  IVehicleShort,
  Replacement,
} from '../../../interface';
import VehicleList from '../VehicleList';
import FilterDropdown from '../../FilterDropdown';
import FilterResult from '../../FilterResult';
import ToggleFilter from '../../ToggleFilter';
import style from './index.module.scss';
import instance from '../../../services/httpRequest';
import analyseData from '../../../utils/analyseSearchData';
import Loading from '../../Loading';
import localesReplacement from '../../../utils/localesReplacement';

interface IProps {
  country?: string;
  replacements: Replacement;
  onVehicleSelect?: (value: string | number, model: string, vehicleType?: string) => void;
  categoriesType: string;
  selectedWorkshop: boolean;
  restore?: boolean;
  preserved?: boolean;
}

const cx = classnames.bind(style);
const classDropdownItem = 'dropdown-item';

function VehicleSearch(props: IProps) {
  const {
    replacements,
    onVehicleSelect,
    categoriesType,
    selectedWorkshop,
    restore,
    preserved,
  } = props;
  const [vehicleTypes, setVehicleTypes] = useState<IVehicleCategory[]>();
  const [typeID, setTypeID] = useState<number | string>();
  const [makes, setMakes] = useState<IMake[]>([]);
  const [make, setMake] = useState<string>('');
  const [models, setModels] = useState<IModel[]>([]);
  const [model, setModel] = useState<string>('');
  const [year, setYear] = useState<string>('');
  const [vehicles, setVehicles] = useState<IVehicleShort[]>([]);
  const [filterVisible, setFilterVisible] = useState<boolean>(true);
  const [isMount, setIsMount] = useState<boolean>(false);
  const type = _.chain(vehicleTypes).find(['id', typeID]).get('name').value();
  const controller = new AbortController();

  const memoDate = useCallback(() => {
    setIsMount(true);
    selectedWorkshop
      ? _.assign(instance.defaults.headers, {isWorkshopOwner: selectedWorkshop})
      : _.unset(instance.defaults.headers, 'isWorkshopOwner');
    instance
      .get('/categories', {signal: controller.signal})
      .then((d) => {
        setVehicleTypes(d.data);
        sessionStorage.getItem('searchTypeId')
          ? setTypeID(sessionStorage.getItem('searchTypeId') ?? '')
          : setTypeID(d.data[0]?.id);
      })
      .then(() => setIsMount(false)).catch(()=>{
            window.dataLayer.push({
              'event' : 'detailsUnavailable_vehicleSearches',
              'pageType' : 'product finder',
              'searchType' : 'vehicle search',
              'userType' : sessionStorage.getItem('selectedType')==='true'?'workshop owner':'vehicle owner',
              'countryCode' : localStorage.getItem('countryCode')
            });

      })
  }, [selectedWorkshop]);

  useEffect(() => {
    return () => {
      controller.abort();
    };
  }, []);
  useEffect(() => {
    let isMounted = true;
    typeID &&
      instance
        .get(`/makes?categoryId=${typeID}`, {signal: controller.signal})
        .then((d) => {
          isMounted && setMakes(d.data);
          isMounted && setIsMount(false);
        });
    return () => {
      isMounted = false;
    };
  }, [typeID]);
  useEffect(() => {
    make &&
      instance
        .get(
          `/models?makeId=${_.chain(_.map(makes))
            .find(['name', make])
            .get('id')}`,
          {signal: controller.signal},
        )
        .then((d) => setModels(d.data));
  }, [make]);
  useEffect(() => {
    model &&
      instance
        .get(
          `/vehicleSearch?modelId=${_.chain(models)
            .map((m) => {
              const $modal = m['model'];
              const $yearFrom = m['yearFrom'];
              const $yearTo = m['yearTo'] ?? '';
              return ({
                ...m,
                model: `${$modal} (${$yearFrom} - ${$yearTo})`
              })
            })
            .find(['model', model])
            .get('id')}`,
          {signal: controller.signal},
        )
        .then((d) => setVehicles(d.data));
  }, [model]);
  const changeVehicleType = (id: number) => {
    setTypeID(id);
    setMakes([]);
    setMake('');
    setModels([]);
    setModel('');
    setVehicles([]);
    setYear('');
    restore && sessionStorage.setItem('searchTypeId', _.toString(id));
    restore && sessionStorage.removeItem('searchMake');
    restore && sessionStorage.removeItem('searchModel');
    restore && sessionStorage.removeItem('searchYear');
  };
  const changeMake = (make: string) => {
    setMake(make);
    setModels([]);
    setModel('');
    setVehicles([]);
    setYear('');
    restore && sessionStorage.setItem('searchMake', make);
    restore && sessionStorage.removeItem('searchModel');
    restore && sessionStorage.removeItem('searchYear');
  };
  const window:any=globalThis;
  window.dataLayer = window.dataLayer || [];
  const changeModel = (model: string) => {
    const vehicletype=vehicleTypes?.find((d)=>d.id === typeID);
    setModel(model);
    setVehicles([]);
    setYear('');
    restore && sessionStorage.setItem('searchModel', model);
    restore && sessionStorage.removeItem('searchYear');
    
window.dataLayer.push({
	'event' : 'vehicleSearches',
	'pageType' : 'product finder',
	'searchType' : 'vehicle search',
	'userType' : selectedWorkshop?'workshop owner' : 'vehicle owner',
	'vehicleCategory' :vehicletype?.name ,
	'vehicleMake' :make ,
	'vehicleModel' :model ,
	'countryCode' :localStorage.getItem('countryCode')
});

  };

  const preservedRef = useRef<boolean>();
  useEffect(() => {
    memoDate();
    if (!preserved || _.isEqual(preservedRef.current, preserved)) {
      setMakes([]);
      setMake('');
      setModels([]);
      setModel('');
      setVehicles([]);
      setYear('');
      restore && sessionStorage.removeItem('searchTypeId');
      restore && sessionStorage.removeItem('searchMake');
      restore && sessionStorage.removeItem('searchModel');
      restore && sessionStorage.removeItem('searchYear');
    }
    preservedRef.current = preserved;
  }, [selectedWorkshop, preserved]);

  useEffect(() => {
    if (preserved) {
      sessionStorage.getItem('searchTypeId') &&
        setTypeID(
          _.toNumber(sessionStorage.getItem('searchTypeId')) ?? undefined,
        );
    }
  }, [preserved]);
  useEffect(() => {
    if (preserved && !_.isEmpty(makes)) {
      sessionStorage.getItem('searchMake') &&
        setMake(sessionStorage.getItem('searchMake') ?? '');
    }
  }, [preserved, makes]);
  useEffect(() => {
    if (preserved && !_.isEmpty(models)) {
      sessionStorage.getItem('searchModel') &&
        setModel(sessionStorage.getItem('searchModel') ?? '');
    }
  }, [preserved, models]);
  useEffect(() => {
    if (preserved && !_.isEmpty(vehicles)) {
      sessionStorage.getItem('searchYear') &&
        setYear(sessionStorage.getItem('searchYear') ?? '');
    }
  }, [preserved, vehicles]);

  return (
    <>
      {!isMount ? (
        <>
          {!!vehicleTypes && !!typeID && (
            <>
              <VehicleList
                dataList={vehicleTypes}
                selected={typeID}
                changeVehicleTypeHandler={changeVehicleType}
                categoriesType={categoriesType}
              />
              <div className={cx('dropdown-list')}>
                <div className={cx(classDropdownItem)}>
                  <FilterDropdown
                    dataList={makes}
                    filter={make}
                    changeHandler={changeMake}
                    type={localesReplacement(replacements, 'Make')}
                    replacements={replacements}
                  />
                </div>
                <div className={cx(classDropdownItem)}>
                  <FilterDropdown
                    dataList={_.chain(models)
                      .map((d) => ({
                        id: d['id'],
                        name: d['model'],
                        yearFrom: d['yearFrom'],
                        yearTo: d['yearTo'],
                      }))
                      .uniqBy((elem) => [elem.yearFrom, elem.yearTo, elem.name].join())
                      .map(
                        (m) =>
                          m['name'] +
                          ` (${m['yearFrom']} - ${m['yearTo'] ?? ''})`,
                      )
                      .value()}
                    filter={model}
                    changeHandler={changeModel}
                    type={localesReplacement(replacements, 'Model')}
                    replacements={replacements}
                  />
                </div>
                <ToggleFilter
                  toggleHandler={setFilterVisible}
                  panelVisible={filterVisible}
                />
                <div className={cx(classDropdownItem, {hidden: !filterVisible})}>
                  <FilterDropdown
                    dataList={analyseData.year(vehicles)}
                    filter={year}
                    changeHandler={(v) => {
                      setYear(v);
                      sessionStorage.setItem('searchYear', v);
                    }}
                    type={localesReplacement(replacements, 'Year')}
                    replacements={replacements}
                  />
                </div>
              </div>
            </>
          )}
          {!!vehicles && type && make && model && (
            <FilterResult
              dataList={_.map(vehicles, (d) => ({
                ...d,
                vehicleType: type,
                make: make,
                model: model,
              }))}
              vehicleType={type}
              make={make}
              model={model}
              year={year}
              onSelect={onVehicleSelect}
              replacements={replacements}
            />
          )}
        </>
      ) : (
        <Loading />
      )}
    </>
  );
}

export default VehicleSearch;
